import { useTranslators } from "commonUse/Locale";
import { useToggle } from "commonUse/tools";
import TReportLink, { Iconfont, TButton, TSpin } from "components/v2/TinyComps";
import Mixpanel from "landings/Analytics/Mixpanel";
import TransferInfoItem from "landings/V2/CircleDW/Deposit/TransferInfoItem";
import { useSignatureDWAPI } from "landings/V2/SignatureDW/common";
import { BeneficiaryRes } from "landings/V2/SignatureDW/types";
import { withMaintenance } from "landings/V2/USDDW/components/Maintenance";
import NoKyc, { useNoKycVisible } from "landings/V2/USDDW/components/NoKyc";
import { DWPayMethod } from "landings/V2/USDDW/types";
import React, { useEffect, useMemo, useState } from "react";
import ResultInPage from "src/components-v2/ResultInPage";
import { requestWithRetry } from "trade_utils_lib";
import { QuotaDirection } from "utils/complianceQuota";
import cs from "./index.module.less";

const TUTORIAL_URL = "https://www.pionex.us/blog/how-to-deposit-usd-via-wire-transfer/";

interface Props {
    beneficiaries: BeneficiaryRes;
}

/**
 * Signature 入金页面
 */
const Deposit: React.FC<Props> = ({ beneficiaries }) => {
    const { $st } = useTranslators();

    useEffect(() => {
        Mixpanel.report.visitUSWireDeposit();
    }, []);

    const beforeDWVisible = useNoKycVisible();
    const [completed, { setTrue: setCompleted, setFalse: setUnCompleted }] = useToggle();
    useEffect(() => {
        // 切换到成功状态自动滚动到顶部
        if (!completed) {
            return;
        }
        setTimeout(() => {
            document.documentElement.scroll({
                top: 0,
                behavior: "smooth",
            });
        }, 100);
    }, [completed]);

    const infos = useMemo(() => {
        const bank0 = beneficiaries.banks?.[0];
        if (!bank0) {
            return [];
        }

        const { beneficiary, beneficiaryBank } = bank0;

        return [
            { label: $st("signature_dw_transfer_recipient_name"), value: beneficiary.name },
            { label: $st("signature_dw_transfer_recipient_address"), value: `${beneficiary.address1} ${beneficiary.address2}` },
            { label: $st("signature_dw_transfer_account_number"), value: beneficiaryBank.accountNumber },
            { label: $st("signature_dw_bank_name"), value: beneficiaryBank.name },
            { label: $st("signature_dw_transfer_bank_address"), value: beneficiaryBank.address },
            { label: $st("signature_dw_transfer_aba"), value: beneficiaryBank.routingNumber },
            { label: $st("signature_dw_transfer_swift"), value: beneficiaryBank.swiftCode },
        ];
    }, [$st, beneficiaries?.banks]);

    if (beforeDWVisible) {
        return <NoKyc reportEvent="fiatdeposit" />;
    }

    if (completed) {
        return (
            <>
                <ResultInPage className={cs.result} type={"success"} des={$st("signature_dw_deposit_success")} />
                <TButton type="bordered" className={cs.again} onClick={setUnCompleted}>
                    {$st("circledw_withdraw_again")}
                </TButton>
            </>
        );
    }

    return (
        <>
            <div className={cs.title}>{$st("signature_deposit_title")}</div>
            <div className={"text-accent text-base"}>{$st("signature_deposit_subtitle")}</div>
            <div className={"text-red text-base"}>{$st("signature_deposit_subtitle2")}</div>
            <div className={cs.hint}>
                <div className={cs.hintContent}>
                    <Iconfont icon="iocn_label_defeated" />
                    {$st("signature_dw_deposit_hint", { strong: (t) => <strong>{t}</strong>, br: <br /> })}
                </div>
            </div>
            <div id="circledw_deposit_transfer_infos">
                <div className={cs.subTitle}>
                    {$st("signature_dw_deposit_transfer_info")}{" "}
                    <a
                        href={TUTORIAL_URL}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                            Mixpanel.report.visitUSWireAddTutorial();
                        }}
                    >
                        <Iconfont icon={"iocn_BotTutorial"} />
                        <span>{$st("tutorial")}</span>
                    </a>
                </div>
                <div className={cs.infoItems}>
                    <div id="circledw_deposit_account">
                        <TransferInfoItem className={cs.singleItem} require label={$st("signature_dw_transfer_account_id")} value={beneficiaries?.accountIdentifier} />
                    </div>
                    <div className={cs.divider} />
                    <div className={cs.uniHint}>{$st("signature_dw_uni_hint", { br: <br /> })}</div>
                    {infos.map((item) => (
                        <TransferInfoItem key={item.label} label={item.label} value={item.value} />
                    ))}
                </div>
            </div>
            <div className={cs.tutorial}>
                {$st("signature_dw_tutorial", {
                    tutorial: (content) => (
                        <TReportLink
                            to={TUTORIAL_URL}
                            target="_blank"
                            onClick={() => {
                                Mixpanel.report.visitUSWireTutorial();
                            }}
                        >
                            <strong>{content}</strong>
                        </TReportLink>
                    ),
                })}
            </div>
            <div className={cs.btns}>
                <TButton
                    type={"primary"}
                    size={"large"}
                    onClick={() => {
                        setCompleted();
                        Mixpanel.report.clickUSWireComplete();
                    }}
                >
                    {$st("circledw_transfer_completed_wire")}
                </TButton>
            </div>
        </>
    );
};

const DepositWrapper: React.FC = () => {
    const signatureDWAPI = useSignatureDWAPI();
    const [beneficiaries, setBeneficiaries] = useState<BeneficiaryRes>();
    useEffect(() => {
        if (!signatureDWAPI) {
            return;
        }
        const subscription = requestWithRetry(() => signatureDWAPI.getBeneficiaries()).subscribe((data) => {
            setBeneficiaries(data);
        });
        return () => subscription.unsubscribe();
    }, [signatureDWAPI]);

    return (
        <div className={cs.container}>
            <TSpin spinning={!beneficiaries}>{beneficiaries && <Deposit beneficiaries={beneficiaries} />}</TSpin>
        </div>
    );
};

export default withMaintenance(DepositWrapper, { payMethod: DWPayMethod.WIRE, direction: QuotaDirection.DEPOSIT });
